import React, { useCallback, useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import { Stack } from '@mui/material';
import { BreakdownsResult, EMetric } from 'api/useAnalytics';
import useUsers from 'api/useUsers';
import { EFeatureFlag } from 'constants/enums';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

import { AnalyticsBarsBox } from 'components/AnalyticsBox/AnalyticsBarsBox';
import { AnalyticsBox } from 'components/AnalyticsBox/AnalyticsBox';

import AnalyticsBusinessTabHeader from './AnalyticsBusinessTabHeader/AnalyticsBusinessTabHeader';
import { useAnalyticsData } from './hooks/use-analytics-data';
import { useXAxisDateFormat } from './hooks/useXAxisDateFormat';
import { useAnalyticsChartData } from './useAnalyticsChartData.hook';
import { DEFAULT_DATE_RANGE, getWholeRange } from './utils';

const BREAKDOWNS_ANALYTICS: EMetric[] = [
  EMetric.DEPOSITS_PER_PRICE_POINT,
  EMetric.REVENUE_PER_PRICE_POINT
];

const AnalyticsBusinessTab: React.FC = () => {
  const { publisherId } = useParams();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [previousDateRange, setPreviousDateRange] = useState<
    DateRange | undefined
  >();
  const [attributeFilters, setAttributeFilters] = useState<
    Record<string, string[]>
  >({});

  const { fetchFeatureFlags } = useUsers({ publisherId });
  const shouldDisplaySciPlayAnalytics =
    fetchFeatureFlags.data?.featureFlags?.[
      EFeatureFlag.CHECKOUT_SCIPLAY_ANALYTICS
    ];

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);
  const apiPreviousDateRange = useMemo(
    () => getWholeRange(previousDateRange),
    [previousDateRange]
  );

  const { analytics, previousAnalytics, isLoading, filteredAnalytics } =
    useAnalyticsData(
      apiDateRange,
      apiPreviousDateRange,
      publisherId,
      shouldDisplaySciPlayAnalytics,
      attributeFilters
    );

  const handleDateRangeChange = (value: DateRange) => {
    setDateRange(value);
    setPreviousDateRange(undefined);
  };

  const {
    [EMetric.TOTAL_REVENUE]: totalRevenueAnalytics,
    ...formattedAnalytics
  } = useAnalyticsChartData({
    stats: analytics.getAnalytics.data,
    trends: analytics.getTrends.data,
    previousStats: previousAnalytics.getAnalytics.data,
    previousTrends: previousAnalytics.getTrends.data,
    previousDateRange: previousDateRange,
    currentDateRange: dateRange
  });

  const xAxisDateFormat = useXAxisDateFormat(analytics);

  const handleAttributeFiltersChange = useCallback(
    (filters: Record<string, string[]>) => {
      setAttributeFilters(filters);
    },
    []
  );

  return (
    <Stack direction="column">
      <AnalyticsBusinessTabHeader
        publisherId={publisherId}
        dateRange={dateRange}
        previousDateRange={previousDateRange}
        onDateRangeChange={handleDateRangeChange}
        onPreviousDateRangeChange={setPreviousDateRange}
        onClearPreviousDateRange={() => setPreviousDateRange(undefined)}
        onAttributeFiltersChange={handleAttributeFiltersChange}
      />
      <div className="flex flex-col gap-4">
        <AnalyticsBox
          key={EMetric.TOTAL_REVENUE}
          metricName={EMetric.TOTAL_REVENUE}
          stat={totalRevenueAnalytics?.stat}
          currentTrends={totalRevenueAnalytics?.currentTrends ?? []}
          previousTrends={totalRevenueAnalytics?.previousTrends}
          xAxisDateFormat={xAxisDateFormat}
          isLoading={isLoading}
        />
        <div className="grid grid-cols-3 gap-4">
          {filteredAnalytics.businessAnalytics.map((metricName) => {
            const item = formattedAnalytics[metricName];
            return (
              <AnalyticsBox
                key={metricName}
                metricName={metricName}
                stat={item?.stat}
                currentTrends={item?.currentTrends ?? []}
                previousTrends={item?.previousTrends}
                isLoading={isLoading}
                xAxisDateFormat={xAxisDateFormat}
              />
            );
          })}
          {BREAKDOWNS_ANALYTICS.map((metricName) => {
            const currentBreakdown = analytics.getBreakdowns.data?.find(
              (breakdown: BreakdownsResult) => breakdown.name === metricName
            );
            const previousBreakdown =
              previousAnalytics.getBreakdowns.data?.find(
                (breakdown: BreakdownsResult) => breakdown.name === metricName
              );
            return (
              <AnalyticsBarsBox
                key={metricName}
                isLoading={isLoading}
                metricName={metricName}
                currentBreakdown={
                  currentBreakdown
                    ? currentBreakdown
                    : {
                        name: metricName,
                        type: EStatisticType.NUMBER,
                        values: []
                      }
                }
                comparedBreakdown={previousBreakdown}
                dateRange={dateRange}
                comparedDateRange={previousDateRange}
              />
            );
          })}
        </div>
      </div>
    </Stack>
  );
};

export default AnalyticsBusinessTab;
